/**
 * CodingWalkthrough — first-15-matches onboarding panel.
 *
 * Surfaces the BBB coding flow as a numbered checklist. Auto-opens
 * on the operator's first ~15 unique matches; after that it's only
 * visible if they click the "?" pill, or until they tick "Don't
 * show again".
 *
 * Source of truth is the backend (User.coding_walkthrough_*), so
 * progress follows the operator between laptops / phones. localStorage
 * is kept as a transient fallback for offline / flaky network — the
 * next successful API call always wins.
 */
import { useEffect, useRef, useState } from "react";
import { HelpCircle, X, ChevronRight, Sparkles } from "lucide-react";
import api from "../../api/axios";
import useDialogDismiss from "../../hooks/useDialogDismiss";


const LS_KEY      = "zcd_coding_walkthrough_v1";
const AUTO_LIMIT  = 15;   // # of unique matches that still auto-open the panel

const API_STATE     = "/api/accounts/me/walkthrough/";
const API_MARK_SEEN = "/api/accounts/me/walkthrough/mark-seen/";
const API_DISMISS   = "/api/accounts/me/walkthrough/dismiss/";


function readLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { seen_match_ids: [], dismissed_forever: false };
    const v = JSON.parse(raw);
    return {
      seen_match_ids: Array.isArray(v.seen_match_ids) ? v.seen_match_ids : [],
      dismissed_forever: !!v.dismissed_forever,
    };
  } catch {
    return { seen_match_ids: [], dismissed_forever: false };
  }
}
function writeLocal(s) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(s)); } catch { /* quota */ }
}


// Each step is a short title + a one-or-two-sentence body. Numbered
// so the operator can map "I'm on step 4" to a specific task without
// scrolling back and forth.
const STEPS = [
  {
    title: "Confirm the players on the crease",
    body: "Striker / Non-striker / Bowler badges sit at the top of the centre column. Click any badge to pick from the playing XI. Bowlers who've finished their over quota are filtered out automatically.",
  },
  {
    title: "Start recording (optional)",
    body: "If you've got a camera or capture device plugged in, click Start recording in the left-pane recorder. If you've got a pre-recorded match file, use the file picker instead. Skip this if you only need the scoring (no video).",
  },
  {
    title: "Score the ball outcome",
    body: "Tap a runs button (0, 1, 2, 3, 4, 5, 6) OR an extras chip (wide, no-ball, bye, leg-bye) OR pick a wicket type. You can stack extras + runs on the same ball — e.g. wide + 2 byes.",
  },
  {
    title: "If a wicket, fill the wicket panel",
    body: "Pick the wicket type, the dismissed batter, the bowler credited, and any fielder involved (caught-by / stumped-by / run-out fielder). For run-outs, use the Next Ball Strike Outcome dropdown to declare who's at strike next.",
  },
  {
    title: "Mark Clip in / Clip out (optional, recording mode only)",
    body: "If you want a tight clip rather than the automatic 10-second window, click Clip in right before the bowler delivers and Clip out right after the ball is dead. The buttons light up green once they're stamped.",
  },
  {
    title: "Click Save Ball",
    body: "Single click = 1-second confirmation window (you can cancel during the countdown). Double-click = instant save. For spinners or fast-bowling cycles where 1 sec is still too slow, click the Quick toggle (lightning icon) next to Save Ball — every single click then saves instantly. Backspace also fires Undo.",
  },
  {
    title: "Made a mistake? Undo and Redo",
    body: "Undo wipes the most recently saved ball server-side. Redo restores it. Both buttons live next to the score panel. You can undo any number of balls in sequence; redo only restores the most recently undone one.",
  },
  {
    title: "Open QC to preview clips",
    body: "Click the QC button to review every ball with its clip side-by-side. During live recording, the most recent ~60 seconds of footage is reviewable in QC the moment the next chunk is captured. After Stop, ALL clips become durably reviewable for everyone.",
  },
  {
    title: "Stop recording at innings break",
    body: "Click Stop recording when the innings ends. This finalises the upload and fires the slicer — within about 30 seconds, every ball has a durable cross-device clip in QC. You can keep coding the next innings while clips slice.",
  },
  {
    title: "End the active innings or match",
    body: "MORE → End Active Innings (when all out / overs done) or End Match (final result). The wallet, scorecard, and live-feed all sync automatically once the match closes.",
  },
];


export default function CodingWalkthrough({ matchId }) {
  const [open, setOpen]   = useState(false);
  const [step, setStep]   = useState(0);
  const panelRef = useRef(null);
  useDialogDismiss({ open, onClose: () => setOpen(false), ref: panelRef });

  // Decide whether to auto-open on this mount. Backend is the source
  // of truth; on network failure we fall back to localStorage so an
  // offline operator still gets the walkthrough.
  useEffect(() => {
    if (!matchId) return;
    let alive = true;
    (async () => {
      let st;
      try {
        const r = await api.post(API_MARK_SEEN, { match_id: matchId });
        st = r.data;
      } catch {
        // Network blip — read local + locally increment.
        const local = readLocal();
        if (!local.seen_match_ids.includes(matchId)) {
          local.seen_match_ids = [...local.seen_match_ids, matchId].slice(-200);
          writeLocal(local);
        }
        st = {
          dismissed:       local.dismissed_forever,
          seen_count:      local.seen_match_ids.length,
          auto_open_limit: AUTO_LIMIT,
          was_new:         true,
        };
      }
      if (!alive) return;
      // Dual-source dismiss check: backend wins if it says dismissed,
      // but a localStorage flag from a previous dismiss attempt that
      // failed to reach the server ALSO suppresses auto-open. Without
      // this, a flaky POST on the prior session leaves dismissed=false
      // on the server and the walkthrough re-opens forever on every
      // new match — which is exactly the "Don't show again doesn't
      // work" symptom operators reported.
      const localState = readLocal();
      if (st.dismissed || localState.dismissed_forever) {
        // If the local flag is true but the server didn't agree, retry
        // the dismiss POST so future sessions converge to the same
        // answer (server becomes the source of truth eventually).
        if (localState.dismissed_forever && !st.dismissed) {
          api.post(API_DISMISS).catch(() => {});
        }
        return;
      }
      // Only auto-open if THIS mount marked it as a new match AND the
      // operator is still within the auto-open window. Backend returns
      // was_new so we don't re-open on every page refresh.
      const limit = st.auto_open_limit ?? AUTO_LIMIT;
      if (st.was_new && st.seen_count <= limit) {
        setOpen(true);
        setStep(0);
      }
    })();
    return () => { alive = false; };
  }, [matchId]);

  const dismissForever = async () => {
    // Close the modal IMMEDIATELY so the operator gets unambiguous
    // feedback even on a flaky network. Previously the close waited
    // for the await, which made the button feel broken when the POST
    // stalled — the source of every "Don't show again does nothing"
    // bug report. Then mirror to localStorage so future loads honour
    // the choice even if the API call fails, and fire-and-forget the
    // POST so the server eventually agrees.
    setOpen(false);
    const local = readLocal();
    local.dismissed_forever = true;
    writeLocal(local);
    try {
      await api.post(API_DISMISS);
    } catch {
      // Network failed — localStorage already has the flag, and the
      // mount-time effect will retry the POST next time the operator
      // opens a match. No further action here.
    }
  };

  return (
    <>
      {/* Always-available floating pill so the operator can re-open
          the walkthrough at any time after they've dismissed it. */}
      <button
        onClick={() => { setOpen(true); setStep(0); }}
        // 2026-05-28 — lifted above the BBP action bar on mobile and
        // stacked above the History pill (which sits at bottom-24).
        // bottom-36 = 144px, leaves a clean gap above the History
        // pill at bottom-24 (96px). 2026-06-05 — desktop bumped from
        // xl:bottom-4 to xl:bottom-36 so the "?" pill stacks above the
        // History pill (now at xl:bottom-20) AND the ScorerChat FAB at
        // bottom-3. All three buttons now stack cleanly bottom-right.
        className="fixed bottom-36 xl:bottom-36 right-4 z-[60] flex items-center gap-1 px-3 py-2 rounded-full bg-bg-primary border border-border-default text-xs text-text-secondary hover:text-text-primary hover:border-button-primary shadow-lg"
        title="Open the coding walkthrough"
      >
        <HelpCircle className="w-3.5 h-3.5" />
        How to code
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          role="presentation"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Coding walkthrough"
            className="bg-bg-primary rounded-2xl max-w-lg w-full border border-bg-secondary shadow-2xl flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-5 py-4 border-b border-border-default flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest">
                  <Sparkles className="w-3 h-3" />
                  Coding walkthrough · step {step + 1} of {STEPS.length}
                </div>
                <h3 className="text-lg font-semibold text-text-primary mt-1">{STEPS[step].title}</h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-bg-secondary text-text-secondary hover:text-text-primary"
                title="Close (you can reopen from the pill)"
              >
                <X className="w-4 h-4" />
              </button>
            </header>

            <div className="px-5 py-4 text-sm text-text-primary leading-relaxed">
              {STEPS[step].body}
            </div>

            {/* Step progress dots — operator can also click them to jump. */}
            <div className="px-5 pb-2 flex flex-wrap gap-1">
              {STEPS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === step
                      ? "w-8 bg-amber-400"
                      : i < step
                        ? "w-4 bg-amber-400/60"
                        : "w-4 bg-bg-secondary"
                  }`}
                  title={STEPS[i].title}
                />
              ))}
            </div>

            <footer className="px-5 py-3 border-t border-border-default flex items-center justify-between gap-2">
              <button
                onClick={dismissForever}
                className="text-[11px] text-text-secondary hover:text-text-primary underline-offset-2 hover:underline"
                title="Hide this for good. The ? pill stays."
              >
                Don't show again
              </button>
              <div className="flex items-center gap-2">
                {step > 0 && (
                  <button
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    className="text-xs px-3 py-1.5 rounded-md bg-bg-secondary text-text-primary hover:bg-bg-main"
                  >
                    Back
                  </button>
                )}
                {step < STEPS.length - 1 ? (
                  <button
                    onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-button-primary text-white hover:opacity-90"
                  >
                    Next
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button
                    onClick={() => setOpen(false)}
                    className="text-xs px-3 py-1.5 rounded-md bg-button-primary text-white hover:opacity-90"
                  >
                    Got it
                  </button>
                )}
              </div>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
